import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PHOTOS } from "@/lib/assets";

const CONCEPTS = [
  { num: "01", title: "Construye tu imagen" },
  { num: "02", title: "Explora tu potencial comercial" },
  { num: "03", title: "Desarrolla tu proyecto" },
];

const SERVICES_PREVIEW = [
  "Imagen y posicionamiento",
  "Estrategia y planificación",
  "Edición y administración del contenido",
  "Marketing y promoción",
  "Gestión de cuentas",
  "Reportes y pagos",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="min-h-[85vh] flex items-center relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
          <div className="lg:col-span-7">
            <Reveal>
              <span
                data-testid="hero-disclosure-badge"
                className="inline-block bg-plum text-cream text-[11px] uppercase tracking-[0.2em] px-4 py-2 mb-8 font-sans"
              >
                Agencia de modelos y creación de contenido · +18
              </span>
              <h1 className="font-serif font-light tracking-tight leading-[1.1] text-4xl sm:text-5xl lg:text-7xl text-ink">
                Tu próxima etapa
                <br />
                <span className="italic text-mauve">empieza contigo.</span>
              </h1>
              <p className="mt-8 text-base sm:text-lg font-light text-stone2 leading-relaxed max-w-xl">
                Potencia tu imagen, desarrolla tu proyecto digital y explora nuevas oportunidades de ingresos con un
                equipo que acompaña tu crecimiento.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/postula"
                  data-testid="hero-cta-oportunidad"
                  className="bg-plum text-ivory hover:bg-mauve px-8 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 font-sans inline-flex items-center justify-center gap-2"
                >
                  Quiero conocer la oportunidad <ArrowRight size={15} />
                </Link>
                <Link
                  to="/nosotros"
                  data-testid="hero-cta-agencia"
                  className="border border-plum/30 text-ink hover:border-mauve hover:text-mauve px-8 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 font-sans inline-flex items-center justify-center"
                >
                  Conoce nuestra agencia
                </Link>
              </div>
              <p
                data-testid="hero-disclosure-note"
                className="mt-10 border-l-2 border-champagne pl-4 text-xs text-stone2 leading-relaxed max-w-md"
              >
                Representación y gestión de modelos y creadoras de contenido digital. Exclusivo para
                mayores de 18 años.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative">
            <Reveal delay={0.2}>
              <div className="absolute -top-4 -left-4 w-full h-full border border-champagne" aria-hidden="true" />
              <img
                src={PHOTOS.hero}
                alt="Marjolaine Rachel, Fundadora y Directora de Exclusive Society"
                data-testid="hero-image"
                className="relative w-full h-auto"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECCIÓN 2 — ESTRATEGIA */}
      <section className="py-20 lg:py-32 bg-cream" data-testid="section-estrategia">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">Estrategia</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-4 max-w-2xl leading-[1.15]">
              Tu ambición merece una estrategia.
            </h2>
            <p className="mt-8 text-base font-light text-stone2 leading-relaxed max-w-2xl">
              Quizás quieres comenzar un proyecto propio, explorar otra fuente de ingresos o dar un paso más en tu
              presencia digital. En Exclusive Society te ayudamos a organizar esa idea con estrategia, edición,
              marketing y gestión personalizada.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-16">
            {CONCEPTS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12}>
                <div
                  data-testid={`concept-card-${c.num}`}
                  className="bg-ivory border border-line p-8 lg:p-10 h-full group hover:border-mauve/50 transition-colors duration-500"
                >
                  <span className="font-serif text-5xl text-champagne group-hover:text-dustyrose transition-colors duration-500">
                    {c.num}
                  </span>
                  <h3 className="font-serif text-2xl text-ink mt-6 leading-snug">{c.title}.</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECCIÓN 3 — PODER DE ELEGIR */}
      <section className="py-20 lg:py-32 bg-plum text-ivory" data-testid="section-eleccion">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <img
                src={PHOTOS.fashion}
                alt="Trabajo creativo en estudio de moda"
                data-testid="eleccion-image"
                className="w-full h-[380px] sm:h-[480px] object-cover object-top"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-8">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] text-dustyrose font-sans font-semibold">
                Autonomía
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.15]">
                El poder de elegir tu propio camino.
              </h2>
              <p className="mt-8 text-base font-light text-[#C9B8BE] leading-relaxed max-w-xl">
                Creemos en mujeres que toman decisiones sobre su futuro. Empoderarte significa conocer tus opciones,
                definir tus límites y elegir qué quieres construir. Nuestro trabajo es acompañar ese proceso con
                claridad, respeto y una gestión profesional.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECCIÓN 4 — SERVICIOS PREVIEW */}
      <section className="py-20 lg:py-32" data-testid="section-servicios-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">
                  Servicios
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-4 leading-[1.15]">
                  Crear es solo una parte. La estrategia hace el resto.
                </h2>
                <p className="mt-8 text-base font-light text-stone2 leading-relaxed">
                  Detrás de un proyecto digital hay planificación, edición, promoción y seguimiento. Nuestro equipo
                  organiza la operación acordada para que puedas concentrarte en crear el contenido que decides
                  compartir.
                </p>
                <Link
                  to="/servicios"
                  data-testid="servicios-preview-link"
                  className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-sans font-semibold text-ink border-b border-dustyrose pb-2 hover:text-mauve hover:border-mauve transition-colors duration-300"
                >
                  Ver todos los servicios <ArrowUpRight size={15} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line">
                {SERVICES_PREVIEW.map((s, i) => (
                  <Reveal key={s} delay={i * 0.08}>
                    <div
                      data-testid={`service-preview-${i + 1}`}
                      className="bg-ivory p-6 lg:p-8 h-full hover:bg-cream transition-colors duration-500"
                    >
                      <span className="text-[10px] uppercase tracking-[0.25em] text-mauve font-sans">
                        0{i + 1}
                      </span>
                      <h3 className="font-serif text-xl text-ink mt-3 leading-snug">{s}</h3>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECCIÓN 5 — FUNDADORA */}
      <section className="py-20 lg:py-32 bg-cream" data-testid="section-fundadora">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <Link to="/nosotros" data-testid="founder-preview-marjolaine" className="group block">
                  <div className="relative">
                    <div className="absolute -top-4 -left-4 w-full h-full border border-champagne" aria-hidden="true" />
                    <img
                      src={PHOTOS.founder_urban}
                      alt="Marjolaine Rachel, Fundadora y Directora de Exclusive Society"
                      className="relative w-full h-auto"
                    />
                  </div>
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">Nosotros</span>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-4 leading-[1.15]">
                  La persona detrás de tu proyecto.
                </h2>
                <h3 className="font-serif text-2xl text-ink mt-10">Marjolaine Rachel</h3>
                <p className="text-xs uppercase tracking-[0.2em] text-mauve font-sans font-semibold mt-1">
                  Fundadora &amp; Directora de Exclusive Society
                </p>
                <blockquote className="mt-8 border-l-2 border-dustyrose pl-6 space-y-5 text-base font-light text-stone2 leading-relaxed max-w-xl">
                  <p>
                    Detrás de Exclusive Society existe una visión clara: crear oportunidades para mujeres que desean
                    crecer, desarrollar su potencial y construir su propia independencia.
                  </p>
                  <p>
                    Marjolaine Rachel lidera este proyecto con una mirada empresarial, femenina y estratégica,
                    combinando creatividad, innovación y una visión moderna del éxito.
                  </p>
                </blockquote>
                <Link
                  to="/nosotros"
                  data-testid="founders-cta-nosotros"
                  className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-sans font-semibold text-ink border-b border-dustyrose pb-2 hover:text-mauve hover:border-mauve transition-colors duration-300"
                >
                  Conoce nuestra historia <ArrowUpRight size={15} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECCIÓN 6 — CTA FINAL */}
      <section className="py-20 lg:py-32" data-testid="section-cta-final">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="border border-champagne bg-gradient-to-br from-ivory to-cream p-10 lg:p-20 text-center">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-[1.15]">
                Descubre qué podrías construir.
              </h2>
              <p className="mt-6 text-base font-light text-stone2 leading-relaxed max-w-xl mx-auto">
                Conversemos sobre tus objetivos, tus dudas y el apoyo que necesitas. La primera reunión es informativa
                y no te obliga a contratar.
              </p>
              <Link
                to="/postula"
                data-testid="cta-final-reunion"
                className="mt-10 inline-flex items-center gap-2 bg-plum text-ivory hover:bg-mauve px-10 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 font-sans"
              >
                Solicitar una reunión <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
